import { useEffect, useState, type ComponentType } from "react";
import { Box, Typography } from "@mui/material";
import SignatureBaseComponent from "../../../common/SignatureBaseComponent";
import { dataURLtoBlob } from "../../../jobs/jobs-list/jobs-layout/components/ForRevision/helpers";
import { QuoteProcessingStatus } from "../../../jobs/jobs-list/jobs-layout/components/ForRevision/QuoteProcesingStatus";
import { useQuoteAcceptMutation } from "../../hooks/useQuoteAcceptMutation";
import { useAuthContextualToken } from "../../hooks/useAuthContextualToken";
import { DepositCell as DepositCellJs } from "../../QuotesTable/DepositCell";
import { QuoteActions } from "./layout/QuoteActions";
import { QuoteClientSection } from "./layout/QuoteClientSection";
import { QuoteHeader } from "./layout/QuoteHeader";
import { QuoteLayout } from "./layout/QuoteLayout";
import { QuoteTotalSummary } from "./layout/QuoteTotalSummary";
import { QuoteServicesRenderer } from "./variants/QuoteServicesRenderer";
import { QuotePdfButtons } from "./layout/QuotePdfButtons";
import { getQuoteGrandTotalFromQuote } from "../../../invoice/view/utils/quoteGrandTotal";
import { IRoles } from "src/utils";
import { quoteModern } from "./style/style";

const DepositCell = DepositCellJs as unknown as ComponentType<{
  quote: Record<string, unknown>;
  lockDepositAddActions?: boolean;
}>;

export const QuoteSummary = ({ quote, role }) => {
  const { accessedByToken } = useAuthContextualToken();
  const [localQuote, setLocalQuote] = useState(quote);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedTimes, setSelectedTimes] = useState({});
  const [status, setStatus] = useState(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState(quote?.signUrl);
  const [validSign, setValidSign] = useState(!quote?.requiredSign);

  const acceptQuote = useQuoteAcceptMutation();

  useEffect(() => {
    setLocalQuote(quote);
    setSignatureDataUrl(quote?.signUrl);
    setValidSign(!quote?.requiredSign || Boolean(quote?.signUrl));
  }, [quote]);

  if (!quote) return null;

  const lockDepositActions = role === "client" || accessedByToken === true;

  const showDepositBlock =
    Boolean(quote?.depositInvoiceUid || (quote?.depositAmount != null && Number(quote.depositAmount) > 0)) && role !== IRoles.Client;

  const grandTotal = getQuoteGrandTotalFromQuote(quote as Record<string, unknown>);

  const totals = {
    subtotal: grandTotal,
    total: grandTotal,
  };

  const transformSelectedTimes = () => {
    return Object.entries(selectedTimes).map(([uid, eventTime]) => ({
      uid,
      eventTime,
    }));
  };

  const handleAccept = (payload) => {
    setStatus(null);
    setIsProcessing(true);

    const formData = new FormData();

    formData.append("quoteId", payload.quoteId);
    formData.append("status", "approved");

    if (selectedTimes) {
      formData.append("services", JSON.stringify(transformSelectedTimes()));
    }

    if (signatureDataUrl) {
      const blob = dataURLtoBlob(signatureDataUrl);
      if (blob) formData.append("signFile", blob, "signature.png");
    }

    acceptQuote.mutate(
      {
        quoteId: payload.quoteId,
        data: formData,
      },
      {
        onSuccess: (response) => {
          setStatus("success");
          setIsProcessing(false);
          setLocalQuote(response.data);
        },
        onError: () => {
          setStatus("error");
          setIsProcessing(false);
        },
      },
    );
  };
  const coverageAreaId =
    quote?.coverageAreaId || quote?.services?.find((service: any) => service?.scopeOfWork?.coverageAreaId)?.scopeOfWork?.coverageAreaId;

  return (
    <QuoteLayout role={role}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
        }}
      >
        <QuotePdfButtons quoteUri={quote.quoteUri} />
      </Box>

      <QuoteHeader quote={quote} />

      <QuoteClientSection client={quote.client} coverageArea={quote.coverageArea} />

      {showDepositBlock && (
        <Box
          sx={{
            ...quoteModern.cardSx,
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Box sx={quoteModern.iconBoxSx}>$</Box>

            <Box>
              <Typography sx={quoteModern.labelSx}>Deposit</Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 14,
                  fontWeight: 800,
                  color: quoteModern.colors.textPrimary,
                  lineHeight: 1.2,
                }}
              >
                Deposit referenced for this estimate
              </Typography>
            </Box>
          </Box>

          <DepositCell quote={quote} lockDepositAddActions={lockDepositActions} />
        </Box>
      )}

      <QuoteProcessingStatus
        key={status === "success" ? `s-${Date.now()}` : status === "error" ? `e-${Date.now()}` : "none"}
        isProcessing={isProcessing}
        status={status}
        message={status === "error" ? "There was a problem accepting the quote. Please try again." : "Your quote has been successfully approved!"}
        duration={3500}
        onHide={() => setStatus(null)}
      />

      <QuoteServicesRenderer
        quote={quote}
        role={role}
        selectedTimes={selectedTimes}
        onSelectTime={(serviceId, time) => {
          setSelectedTimes((prev) => ({
            ...prev,
            [serviceId]: time,
          }));
        }}
      />

      <QuoteTotalSummary quote={totals} services={quote.services || []} />

      {quote?.requiredSign && (
        <Box
          sx={{
            ...quoteModern.cardSx,
            mt: 2.5,
          }}
        >
          <SignatureBaseComponent
            role={role}
            containerSx={{ mt: 0 }}
            height={200}
            label="Signature required for quote"
            initialValue={signatureDataUrl}
            onSave={(url) => {
              setSignatureDataUrl(url);
              setValidSign(true);
            }}
          />
        </Box>
      )}

      <QuoteActions
        role={role}
        enableAdvanced={true}
        quote={localQuote}
        selectedTimes={selectedTimes}
        validSign={validSign}
        onAccept={handleAccept}
        onSupport={() => {}}
      />
    </QuoteLayout>
  );
};
